define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Util/json2Array',
    '../Util/array2Json',
    '../System/ColorObj',
    '../System/Vec3Obj',
    '../Enum/EnumPolygonEditMode',
    '../Enum/EnumPolygonEffectMode'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    json2Array,
    array2Json,
    ColorObj,
    Vec3Obj,
    EnumPolygonEditMode,
    EnumPolygonEffectMode
) {
    'use strict'

    /**
     * 面。
     * <br /><br />
     * <div align='center'>
     * <img src='images/Polygon.gif' width='900' height='650' /><br />
     * 面效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/Polygon.html|CooWasm Polygon demo}
     * @alias Polygon
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumPolygonEditMode} [props.editMode=EnumPolygonEditMode.CREATE] 编辑状态，如果points为空，则需要通过鼠标点击场景创建；如果points不为空，则为展示模式。
     * @param {Array.<Vec3Obj>} [props.points=null] 当editMode为SHOW时，必须传入相应点位构成面。
     * @param {ColorObj|String} [props.color=new ColorObj(1.0, 0.0, 0.0, 0.6)] 面颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {ColorObj|String} [props.pointColor=new ColorObj(1.0, 1.0, 0.0, 0.9)] 关键点颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.pointSize=3.0] 关键点大小。
     * @param {ColorObj|String} [props.lineColor=new ColorObj(1.0, 0.0, 0.0, 0.9)] 边框线颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.lineWidth=2.0] 边框线宽度。
     * @param {Boolean} [props.bLineVisible=true] 边框线是否显示。
     * @param {Number} [props.minEffect=-10.0] 渲染最低影响高度（绝对海拔）。
     * @param {Number} [props.maxEffect=100.0] 渲染最高影响高度（绝对海拔）。
     * @param {Number} [props.minDistance=1.0] 最小可视距离。
     * @param {Number} [props.maxDistance=20000.0] 最大可视距离。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {EnumPolygonEffectMode} [props.effectMode=EnumPolygonEffectMode.NULL] 渲染效果模式。
     * @param {Number} [props.effectTime=0.0] 特效持续时间。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * var polygon = new CooWasm.Polygon({},(state)=>{console.log("load status:" + state)})
     */
    class Polygon {
        /**
         * @description 编辑状态，如果points为空，则需要通过鼠标点击场景创建；如果points不为空，则为展示模式。
         * @type {EnumPolygonEditMode}
         */
        editMode
        /**
         * @description 当editMode为SHOW时，必须传入相应点位构成面。
         * @type {Array.<Vec3Obj>}
         */
        points
        /**
         * @description 面颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        color
        /**
         * @description 关键点颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        pointColor
        /**
         * @description 关键点大小。
         * @type {Number}
         */
        pointSize
        /**
         * @description 边框线颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        lineColor
        /**
         * @description 边框线宽度。
         * @type {Number}
         */
        lineWidth
        /**
         * @description 边框线是否显示。
         * @type {Boolean}
         */
        bLineVisible
        /**
         * @description 渲染最低影响高度（绝对海拔）。
         * @type {Number}
         */
        minEffect
        /**
         * @description 渲染最高影响高度（绝对海拔）。
         * @type {Number}
         */
        maxEffect
        /**
         * @description 最小可视距离。
         * @type {Number}
         */
        minDistance
        /**
         * @description 最大可视距离。
         * @type {Number}
         */
        maxDistance
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 渲染效果模式。
         * @type {EnumPolygonEffectMode}
         */
        effectMode
        /**
         * @description 特效持续时间。
         * @type {Number}
         */
        effectTime

        /**
         * @description 显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true

        /**
         * @description 面对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 面组件绘制uuid。
         * @type {String}
         * @readonly
         */
        componentRegionRenderUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description Polygon在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Polygon"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentRegionRenderingType = "deSMP.AvatarComponent.RegionRenderingComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"

        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            let bSync = true
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.EmptyAvatar"
            let componentCategoryName = Polygon.componentRegionRenderingType + "#$%" + Polygon.componentAltitudeVisCtrlType + "#$%"
            let avatarProps = ""
            let componentProps = ""
            //create avatars
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")

            let regionRenderComponentProps = ""
            regionRenderComponentProps += this.toStr("editMode", props.editMode, "EditMode")
            if (defined(props.points)) {
                this.points = props.points
                regionRenderComponentProps += "Points:" + array2Json('TUPLE3D', this.points) + "|"
            }
            regionRenderComponentProps += this.toStr("effectMode", props.effectMode, "EffectMode")
            regionRenderComponentProps += this.toStr("effectTime", props.effectTime, "EffectDurationTime")
            regionRenderComponentProps += this.toColorStr("color", props.color, "Color")
            regionRenderComponentProps += this.toColorStr("pointColor", props.pointColor, "PointColor")
            regionRenderComponentProps += this.toStr("pointSize", props.pointSize, "PointSize")
            regionRenderComponentProps += this.toColorStr("lineColor", props.lineColor, "LineColor")
            regionRenderComponentProps += this.toStr("lineWidth", props.lineWidth, "LineWidth")
            regionRenderComponentProps += this.toStr("bLineVisible", props.bLineVisible, "LineVisible")
            regionRenderComponentProps += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
            regionRenderComponentProps += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
            regionRenderComponentProps += this.toStr("minEffect", props.minEffect, "LowerExtension")
            regionRenderComponentProps += this.toStr("maxEffect", props.maxEffect, "UpperExtension")
            componentProps += regionRenderComponentProps + "#$%"
            let altitudeVisCtrlComponentProps = ""
            altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componentProps += altitudeVisCtrlComponentProps + "#$%"
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(componentCategoryName), encode(componentProps), bSync, this.bVisible, true)
            //var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), this.bVisible, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentRegionRenderUuid = idArray[1]
                this.componentAltitudeVisCtrlUuid = idArray[2]
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    if (this.editMode == EnumPolygonEditMode.SHOW) {
                        if (defined(callback)) {
                            callback(state)
                        }
                        Element.prototype.Map.polygons.push(this)
                    } else {
                        callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                            if (defined(callback)) {
                                callback(state)
                            }
                            Element.prototype.Map.polygons.push(this)
                        })
                    }
                })
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {EnumPolygonEditMode} [props.editMode] 编辑状态，如果points为空，则需要通过鼠标点击场景创建；如果points不为空，则为展示模式。
         * @param {EnumPolygonEffectMode} [props.effectMode] 渲染效果模式。
         * @param {Array.<Vec3Obj>} [props.points] 当editMode为SHOW时，必须传入相应点位构成面。
         * @param {ColorObj|String} [props.color] 面颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @param {ColorObj|String} [props.pointColor] 关键点颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @param {Number} [props.pointSize] 关键点大小。
         * @param {ColorObj|String} [props.lineColor] 边框线颜色，可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @param {Number} [props.lineWidth] 边框线宽度。
         * @param {Boolean} [props.bLineVisible] 边框线是否显示。
         * @param {Number} [props.minEffect] 渲染最低影响高度（绝对海拔）。
         * @param {Number} [props.maxEffect] 渲染最高影响高度（绝对海拔）。
         * @param {Number} [props.minDistance] 最小可视距离。
         * @param {Number} [props.maxDistance] 最大可视距离。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Number} [props.effectTime] 特效持续时间。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @example
         * polygon.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)})
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            let regionRenderComponentProps = ""
            let altitudeVisCtrlComponentProps = ""
            let uuids = ""
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")

                regionRenderComponentProps += this.toStr("editMode", props.editMode, "EditMode")
                if (defined(props.points)) {
                    this.points = props.points
                    regionRenderComponentProps += "Points:" + array2Json('TUPLE3D', this.points) + "|"
                }
                regionRenderComponentProps += this.toStr("effectMode", props.effectMode, "EffectMode")
                regionRenderComponentProps += this.toStr("effectTime", props.effectTime, "EffectDurationTime")
                regionRenderComponentProps += this.toColorStr("color", props.color, "Color")
                regionRenderComponentProps += this.toColorStr("pointColor", props.pointColor, "PointColor")
                regionRenderComponentProps += this.toStr("pointSize", props.pointSize, "PointSize")
                regionRenderComponentProps += this.toColorStr("lineColor", props.lineColor, "LineColor")
                regionRenderComponentProps += this.toStr("lineWidth", props.lineWidth, "LineWidth")
                regionRenderComponentProps += this.toStr("bLineVisible", props.bLineVisible, "LineVisible")
                regionRenderComponentProps += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
                regionRenderComponentProps += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
                regionRenderComponentProps += this.toStr("minEffect", props.minEffect, "LowerExtension")
                regionRenderComponentProps += this.toStr("maxEffect", props.maxEffect, "UpperExtension")
                //altitudeVisCtrlComponent
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                uuids += this.componentRegionRenderUuid + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"
                componentProps += regionRenderComponentProps + "#$%"
                componentProps += altitudeVisCtrlComponentProps + "#$%"

                if (props.editMode == EnumPolygonEditMode.SHOW) {
                    callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                    })
                } else {
                    callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                    })
                }
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
                // Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentRegionRenderUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        toColorStr(memName, value, key){
            let result = "";
            if (defined(value)) {
                this[memName] = value;
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toHexStr() + "|";
                } else {
                    if (typeof value === 'string') {
                        let color = ColorObj.fromCssColorString(value);
                        if (defined(color)) {
                            value = color.toHexStr();
                            this[memName] = value;
                            result = key + ":" + value + "|";
                        } else {
                            console.warn(`Invalid color value: ${value}. Using default value.`);
                        }
                    }
                }
            }
            return result;
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * Polygon.locate((state)=>{console.log("locate status:" + state)})
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * Polygon.getProperties()
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "RegionRenderingComponent") {
                    this.componentRegionRenderUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "EditMode") {
                            this.editMode = value
                        } else if (name == "EffectMode") {
                            this.effectMode = value
                        } else if (name == "EffectDurationTime") {
                            this.effectTime = Number(value)
                        } else if (name == "Points") {
                            this.points = json2Array(value)
                        } else if (name == "Color") {
                            this.color = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "PointColor") {
                            this.pointColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "PointSize") {
                            this.pointSize = Number(value)
                        } else if (name == "LineColor") {
                            this.lineColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "LineWidth") {
                            this.lineWidth = Number(value)
                        } else if (name == "LineVisible") {
                            if (value == "true" || value == "1") {
                                this.bLineVisible = true
                            } else {
                                this.bLineVisible = false
                            }
                        } else if (name == "MinVisibleDistance") {
                            this.minDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.maxDistance = Number(value)
                        } else if (name == "LowerExtension") {
                            this.minEffect = Number(value)
                        } else if (name == "UpperExtension") {
                            this.maxEffect = Number(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * Polygon = Polygon.destroy((state)=>{console.log("destroy status:" + state)})
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.polygons = Element.prototype.Map.polygons.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("EditEnd", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentRegionRenderUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return Polygon
})